import type { Campaign } from '../shared/model';
import type { Material, PublicationPackage } from '../shared/campaigns';
import {
  assessPackageCta,
  comparePackages,
  ctaRulePath,
  previousPackage,
} from '../shared/package-review';

export function PackageCtaReview({
  campaign,
  pkg,
  materials,
}: {
  campaign: Campaign;
  pkg: PublicationPackage;
  materials: Material[];
}) {
  const assessment = assessPackageCta(campaign, pkg, materials);
  const historical = !pkg.ctaPolicyVersion && pkg.status !== 'em_revisao';
  return (
    <section
      className={`cw-cta-review ${historical ? '' : assessment.status}`}
      aria-label="Revisão de CTA"
    >
      <strong>Revisão de CTA</strong>
      <p>
        {historical
          ? 'Pacote anterior à verificação de CTA. O registro e a decisão original estão preservados; uma nova revisão será conferida pela regra atual.'
          : assessment.message}
      </p>
      {!historical && assessment.issues.length > 0 && (
        <details>
          <summary>Textos sinalizados ({assessment.issues.length})</summary>
          {assessment.issues.map((issue, index) => (
            <div className="cw-cta-excerpt" key={index}>
              <strong>{issue.field}</strong>
              <blockquote>{issue.excerpt}</blockquote>
              <small>
                {issue.covered
                  ? 'Coberto pela exceção GUIA declarada.'
                  : issue.intent === 'request'
                    ? 'Pedido de direct: precisa de revisão do texto ou exceção válida.'
                    : 'Menção: exige conferência da intenção pelo aprovador.'}
              </small>
            </div>
          ))}
        </details>
      )}
      {pkg.ctaReview && (
        <p className="cw-fine">
          Parecer: {pkg.ctaReview.note} · {pkg.ctaReview.reviewedBy}
        </p>
      )}
      <details>
        <summary>Regra editorial</summary>
        <p>
          O CTA padrão usa comentários. A exceção documentada pede GUIA no direct do
          @reinaldoferreiraoficial apenas na campanha oficial do guia. As postagens não exigem aviso
          de suporte. Fontes históricas permanecem intactas.
        </p>
        <small>{ctaRulePath}</small>
      </details>
    </section>
  );
}

export function PackageRevisionDiff({
  pkg,
  packages,
  materials,
}: {
  pkg: PublicationPackage;
  packages: PublicationPackage[];
  materials: Material[];
}) {
  const previous = previousPackage(pkg, packages);
  if (!previous)
    return (
      <p className="cw-fine">
        Não há uma revisão anterior desta peça na campanha disponível para comparar.
      </p>
    );
  const changes = comparePackages(previous, pkg, materials);
  return (
    <details className="cw-package-diff">
      <summary>
        O que mudou desde a revisão {previous.revision} ({changes.length}{' '}
        {changes.length === 1 ? 'campo' : 'campos'})
      </summary>
      {!changes.length ? (
        <p>
          Conteúdo do pacote preservado. Decisões e registros operacionais ficam fora desta
          comparação.
        </p>
      ) : (
        changes.map((change) => (
          <section key={change.field}>
            <h4>{change.label}</h4>
            <div className="cw-diff-columns">
              <div>
                <strong>Antes · revisão {previous.revision}</strong>
                {change.before.map((value, index) => (
                  <pre key={index}>{value}</pre>
                ))}
              </div>
              <div>
                <strong>Agora · revisão {pkg.revision}</strong>
                {change.after.map((value, index) => (
                  <pre key={index}>{value}</pre>
                ))}
              </div>
            </div>
          </section>
        ))
      )}
    </details>
  );
}
